import type { SupabaseClient } from '@supabase/supabase-js';
import { sendVarioMessage } from '../vario-iframe.js';

interface InstallContext {
  contentEl: HTMLElement;
  supabase: SupabaseClient;
  session: { token: string; tenantId: string };
  handshake: { appToken: string; appIdentifier?: string; offlineToken?: string };
  supabaseUrl: string;
}

interface InstallResponse {
  ok?: boolean;
  marketplaceConnectionId?: string;
  isNew?: boolean;
  // Ergebnis der automatischen Verkaufskanal-Anlage (gambio-install stößt gambio-channel-add
  // serverseitig an). ok:false ist NICHT fatal — der Install selbst ist trotzdem erfolgreich.
  channel?:
    | { ok: true; channelId?: string; label?: string; alreadyPresent?: boolean }
    | { ok: false; error: string }
    | null;
  error?: { code: string; message: string; details?: unknown };
}

// Scoped Karten-Design für den Gambio-Install-Dialog. Alles unter `.gambio-install`
// gekapselt (kein globales Klassen-Präfix, kein box-sizing-Leak, keine Kollision mit
// den geteilten v-*/kl-*-Klassen). go-brand-Tokens (#84B933, Outfit/JetBrains Mono -
// Fonts lädt index.html global). Look identisch zur Otto-App (otto-ui.ts), nur mit
// dem Gambio-typischen HTTP-Basic-Zugangsdaten-Formular statt OAuth-Button.
const STYLE_ID = 'gambio-install-styles';

const STYLES = `
.gambio-install{--vario-green:#84B933;--vario-green-dark:#6FA028;--vario-green-tint:#EFF6E2;
  --ink:#1A1A1A;--ink-soft:#3A3A3A;--muted:#737373;--surface:#FFFFFF;--border:#E4E4E7;--field:#F1F1F3;
  --success:#22C55E;--danger:#E5484D;--warn:#F5A623;
  font-family:'Outfit',system-ui,sans-serif;color:var(--ink);display:flex;flex-direction:column;}
.gambio-install *{box-sizing:border-box;}
.gambio-install .mono{font-family:'JetBrains Mono',ui-monospace,monospace;}

.gambio-install .apphead{display:flex;align-items:center;gap:14px;margin-bottom:10px;}
.gambio-install .brand{display:inline-flex;align-items:center;text-decoration:none;flex:none;}
.gambio-install .brand img{height:20px;display:block;}
.gambio-install .hdivider{width:1px;height:24px;background:var(--border);flex:none;}
.gambio-install .heyebrow{font-size:10.5px;font-weight:600;letter-spacing:.11em;text-transform:uppercase;color:var(--muted);}
.gambio-install .htitle h1{font-size:20px;font-weight:600;letter-spacing:-0.01em;margin:0;line-height:1.2;}

.gambio-install .stage{display:flex;align-items:flex-start;justify-content:center;padding:14px 16px;}
.gambio-install .card{width:100%;max-width:680px;background:var(--surface);border-radius:16px;
  box-shadow:0 1px 2px rgba(16,24,40,.04),0 18px 44px -20px rgba(16,24,40,.22);overflow:hidden;}
.gambio-install .card-top{padding:18px 30px;border-bottom:1px solid #F0F0F1;display:flex;align-items:center;gap:18px;}
.gambio-install .gambio-badge{height:34px;flex:none;mix-blend-mode:multiply;}
.gambio-install .card-top .titles{flex:1;min-width:0;}
.gambio-install .card-top h2{font-size:21px;font-weight:700;letter-spacing:-0.015em;margin:0;}
.gambio-install .card-top .sub{font-size:13px;color:var(--muted);margin-top:3px;}
.gambio-install .env-tag{flex:none;display:inline-flex;align-items:center;gap:6px;font-size:11px;font-weight:600;border-radius:999px;padding:4px 11px;background:#FDF1DC;color:#9A6A12;}
.gambio-install .env-tag .d{width:6px;height:6px;border-radius:50%;background:var(--warn);}
.gambio-install .env-tag.prod{background:var(--vario-green-tint);color:var(--vario-green-dark);}
.gambio-install .env-tag.prod .d{background:var(--vario-green);}

.gambio-install .card-body{padding:18px 30px;}
.gambio-install .intro{font-size:13.5px;color:var(--ink-soft);line-height:1.5;margin:0 0 15px;}
.gambio-install .intro code{font-family:'JetBrains Mono',monospace;font-size:12.5px;background:var(--field);border-radius:5px;padding:1px 6px;}

.gambio-install .sec-lab{font-size:11px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;color:#9aa0a6;margin:0 0 11px;display:flex;align-items:center;gap:9px;}
.gambio-install .sec-lab::after{content:"";flex:1;height:1px;background:#F0F0F1;}

.gambio-install .field{display:flex;flex-direction:column;gap:5px;margin-bottom:11px;}
.gambio-install .field label{font-size:11px;font-weight:600;letter-spacing:.04em;text-transform:uppercase;color:var(--muted);display:flex;align-items:center;gap:7px;}
.gambio-install .field .req{color:var(--danger);}
.gambio-install .field .opt{color:#a8acb2;font-weight:500;letter-spacing:0;text-transform:none;}
.gambio-install .inp{font-family:inherit;font-size:14px;color:var(--ink);background:#fff;border:1px solid #E2E2E6;border-radius:9px;height:40px;padding:0 13px;width:100%;transition:border-color .15s,box-shadow .15s;}
.gambio-install .inp::placeholder{color:#a8acb2;}
.gambio-install .inp:focus{outline:none;border-color:var(--vario-green);box-shadow:0 0 0 3px rgba(132,185,51,.14);}
.gambio-install .pwwrap{position:relative;}
.gambio-install .pwwrap .inp{padding-right:42px;}
.gambio-install .pweye{position:absolute;right:10px;top:50%;transform:translateY(-50%);width:26px;height:26px;border:none;background:none;cursor:pointer;color:#9aa0a6;display:flex;align-items:center;justify-content:center;}
.gambio-install .pweye:hover{color:var(--ink-soft);}
.gambio-install .pweye svg{width:17px;height:17px;}
.gambio-install .hint{font-size:11.5px;color:#9aa0a6;line-height:1.5;}
.gambio-install .hint code{font-family:'JetBrains Mono',monospace;font-size:11px;background:var(--field);border-radius:4px;padding:1px 5px;}

.gambio-install .card-foot{padding:13px 30px;border-top:1px solid #F0F0F1;display:flex;align-items:center;gap:12px;flex-wrap:wrap;}
.gambio-install .foot-status{font-size:12.5px;color:var(--muted);display:flex;align-items:center;gap:8px;}
.gambio-install .foot-status svg{width:15px;height:15px;color:#c4c8ce;flex:none;}
.gambio-install .foot-status.ready{color:var(--vario-green-dark);}
.gambio-install .foot-status.ready svg{color:var(--vario-green);}
.gambio-install .foot-status.error{color:var(--danger);}
.gambio-install .foot-status.error svg{color:var(--danger);}
.gambio-install .foot-actions{margin-left:auto;display:flex;gap:11px;}

.gambio-install .btn{font-family:inherit;font-size:13.5px;font-weight:500;border-radius:9px;padding:11px 18px;border:1px solid transparent;cursor:pointer;display:inline-flex;align-items:center;gap:9px;line-height:1;white-space:nowrap;transition:background .2s,box-shadow .2s,border-color .2s;}
.gambio-install .btn svg{width:16px;height:16px;}
.gambio-install .btn-primary{background:var(--vario-green);color:#fff;box-shadow:0 2px 8px -2px rgba(132,185,51,.5);}
.gambio-install .btn-primary:hover:not(:disabled){background:var(--vario-green-dark);box-shadow:0 4px 14px -3px rgba(132,185,51,.55);}
.gambio-install .btn-ghost{background:#fff;color:var(--ink-soft);border-color:#E2E2E6;}
.gambio-install .btn-ghost:hover:not(:disabled){background:#fafafa;border-color:#d2d2d6;}
.gambio-install .btn:disabled{opacity:.5;cursor:not-allowed;box-shadow:none;}
.gambio-install .btn .spin{width:15px;height:15px;border:2px solid rgba(255,255,255,.45);border-top-color:#fff;border-radius:50%;animation:gambio-spin .7s linear infinite;}
@keyframes gambio-spin{to{transform:rotate(360deg);}}

.gambio-install .done-card{text-align:center;padding:48px 30px 40px;}
.gambio-install .done-ic{width:64px;height:64px;border-radius:50%;background:var(--vario-green-tint);display:flex;align-items:center;justify-content:center;margin:0 auto 20px;}
.gambio-install .done-ic svg{width:30px;height:30px;color:var(--vario-green-dark);}
.gambio-install .done-ic.neutral{background:var(--field);}
.gambio-install .done-ic.neutral svg{color:var(--muted);}
.gambio-install .done-card h2{font-size:21px;font-weight:700;margin:0 0 8px;}
.gambio-install .done-card p{font-size:14px;color:var(--muted);margin:0 auto 22px;max-width:400px;line-height:1.6;}
.gambio-install .done-card .mono{font-size:12.5px;color:var(--ink-soft);}

/* Auto-Grow für den Install-Dialog (Marker html.gambio-autogrow, gesetzt in renderInstall).
   Ohne das deckelt bridge.css §16 (html,body,main#app = height:100% + overflow:hidden)
   body.scrollHeight auf die Viewport-Höhe → das iframe meldet Vario nur die Viewport- statt
   der Inhaltshöhe und die Karte (inkl. Foot-Buttons) wird unten abgeschnitten. Höher-
   spezifischer Selektor gewinnt gegen die nackten bridge-Regeln, ohne bridge.css zu ändern.
   Identisch zum otto-autogrow-Muster. */
html.gambio-autogrow, html.gambio-autogrow body, html.gambio-autogrow main#app{height:auto;overflow:visible;}
html.gambio-autogrow main#app > section#content{flex:0 0 auto;}
`;

// Inline-SVGs (stroke-basiert, currentColor) - identisch zur Otto-App.
const ICON = {
  info: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/><path d="M12 8h.01M11 12h1v4h1"/></svg>',
  check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 6 9 17l-5-5"/></svg>',
  checkBold: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M20 6 9 17l-5-5"/></svg>',
  x: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6 6 18M6 6l12 12"/></svg>',
  download: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 3v12M7 10l5 5 5-5M5 21h14"/></svg>',
  eye: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>',
  eyeOff: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 3l18 18M10.6 10.6a3 3 0 0 0 4.2 4.2M9.9 4.6A9.8 9.8 0 0 1 12 5c6.5 0 10 7 10 7a17 17 0 0 1-3.2 4M6.6 6.6A17 17 0 0 0 2 12s3.5 7 10 7a9.6 9.6 0 0 0 3.4-.6"/></svg>',
};

function ensureStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = STYLES;
  document.head.appendChild(el);
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

export async function renderInstall(ctx: InstallContext) {
  ensureStyles();
  // Auto-Grow: iframe = Inhaltshöhe, Vario scrollt außen (Marker + main.ts 'auto'-Modus).
  document.documentElement.classList.add('gambio-autogrow');
  ctx.contentEl.hidden = false;
  ctx.contentEl.innerHTML = `
    <div class="gambio-install">
      <header class="apphead">
        <a class="brand" href="https://www.go-ecommerce.de" target="_blank" rel="noopener" title="go eCommerce"><img src="/logo-black.png" alt="go eCommerce"></a>
        <span class="hdivider"></span>
        <div class="htitle"><div class="heyebrow">Verkaufskanal · Installation</div><h1>App-Installation</h1></div>
      </header>

      <div class="stage">
        <div class="card" id="install-card">
          <div class="card-top">
            <img class="gambio-badge" src="/gambio-logo.png?v=2" alt="Gambio">
            <div class="titles">
              <h2>Gambio-App installieren</h2>
              <div class="sub">Shop-Anbindung über die Gambio REST-API</div>
            </div>
            <span class="env-tag"><span class="d"></span>Einrichtung</span>
          </div>

          <form id="install-form" novalidate>
            <div class="card-body">
              <p class="intro">Hinterlege die Zugangsdaten deines <b>Gambio-Shops</b> (REST-API, HTTP-Basic). Du brauchst einen Gambio-Admin-User mit aktivierten Backend- und API-Rechten. Die Daten werden verschlüsselt gespeichert.</p>

              <div class="sec-lab">Gambio REST-API</div>

              <div class="field">
                <label for="shop-label">Anzeigename <span class="opt">(optional)</span></label>
                <input class="inp" type="text" id="shop-label" name="label" autocomplete="off" placeholder="z. B. Hauptshop">
              </div>

              <div class="field">
                <label for="base-url">Shop-URL <span class="req">*</span></label>
                <input class="inp" type="text" id="base-url" name="baseUrl" autocomplete="off" placeholder="shop.example.de" required>
                <span class="hint">Nur die Shop-Adresse, z. B. <code>shop.example.de</code> oder <code>https://shop.example.de</code>. Protokoll und API-Version werden bei Bedarf automatisch ergänzt.</span>
              </div>

              <div class="field">
                <label for="username">Benutzername <span class="req">*</span></label>
                <input class="inp" type="text" id="username" name="username" autocomplete="off" placeholder="Admin-E-Mail" required>
              </div>

              <div class="field">
                <label for="password">Passwort <span class="req">*</span></label>
                <div class="pwwrap">
                  <input class="inp" type="password" id="password" name="password" autocomplete="off" placeholder="••••••••" required>
                  <button type="button" class="pweye" id="pw-toggle" aria-label="Passwort anzeigen">${ICON.eye}</button>
                </div>
              </div>
            </div>

            <div class="card-foot">
              <span class="foot-status" id="foot-status">${ICON.info}Noch nicht verbunden</span>
              <div class="foot-actions">
                <button type="button" class="btn btn-ghost" id="cancel-btn">${ICON.x}Abbrechen</button>
                <button type="submit" class="btn btn-primary" id="submit-btn">${ICON.download}App installieren</button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  `;

  const card = ctx.contentEl.querySelector<HTMLDivElement>('#install-card')!;
  const form = ctx.contentEl.querySelector<HTMLFormElement>('#install-form')!;
  const footStatus = ctx.contentEl.querySelector<HTMLSpanElement>('#foot-status')!;
  const submitBtn = ctx.contentEl.querySelector<HTMLButtonElement>('#submit-btn')!;
  const cancelBtn = ctx.contentEl.querySelector<HTMLButtonElement>('#cancel-btn')!;
  const pwInput = ctx.contentEl.querySelector<HTMLInputElement>('#password')!;
  const pwToggle = ctx.contentEl.querySelector<HTMLButtonElement>('#pw-toggle')!;

  let abortController: AbortController | null = null;

  function setFoot(kind: 'info' | 'ready' | 'error', text: string) {
    const icon = kind === 'ready' ? ICON.check : ICON.info;
    footStatus.className = kind === 'info' ? 'foot-status' : `foot-status ${kind}`;
    footStatus.innerHTML = `${icon}${escapeHtml(text)}`;
  }

  function setBusy(busy: boolean) {
    submitBtn.disabled = busy;
    submitBtn.innerHTML = busy
      ? `<span class="spin"></span>Prüfe Zugangsdaten …`
      : `${ICON.download}App installieren`;
  }

  // Passwort ein-/ausblenden (kein Browser-Dialog, reiner Sichtbarkeits-Toggle).
  pwToggle.addEventListener('click', () => {
    const show = pwInput.type === 'password';
    pwInput.type = show ? 'text' : 'password';
    pwToggle.innerHTML = show ? ICON.eyeOff : ICON.eye;
    pwToggle.setAttribute('aria-label', show ? 'Passwort verbergen' : 'Passwort anzeigen');
  });

  cancelBtn.addEventListener('click', () => {
    if (abortController) {
      abortController.abort();
      return;
    }
    // Vario bietet KEIN app-seitiges Abbrechen-Signal (bestätigt vom Gambio/Vario-Support,
    // 2026-07-27): `installationComplete: false` schließt den Dialog nicht. `installationComplete:
    // true` blendet lediglich Varios eigenen Schließen-Button ein und ist der vom Hersteller
    // empfohlene Weg, dem Anwender einen Ausgang zu geben. Die Installation behandeln wir intern
    // als abgebrochen: es wurde kein gambio-install-Call abgesetzt → keine Verbindung angelegt.
    // Programmatisches Schließen des Modals durch die App ist nicht möglich. (Vario hat die
    // Framework-Lücke aufgenommen; betrifft auch das „Offline Token erneuern"-Modal.)
    showCancelled(card);
    sendVarioMessage({ installationComplete: true });
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const label = (form.querySelector<HTMLInputElement>('#shop-label')!.value || '').trim();
    const baseUrl = (form.querySelector<HTMLInputElement>('#base-url')!.value || '').trim();
    const username = (form.querySelector<HTMLInputElement>('#username')!.value || '').trim();
    const password = form.querySelector<HTMLInputElement>('#password')!.value || '';

    if (!baseUrl || !username || !password) {
      return setFoot('error', 'Shop-URL, Benutzername und Passwort sind Pflichtfelder.');
    }

    setBusy(true);
    setFoot('info', 'Prüfe Gambio-Zugangsdaten …');
    abortController = new AbortController();

    try {
      const res = await fetch(`${ctx.supabaseUrl.replace(/\/$/, '')}/functions/v1/gambio-install`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${ctx.session.token}`,
        },
        body: JSON.stringify({
          baseUrl,
          username,
          password,
          // Anzeigename des Primärshops (optional; leer → Server-Default 'Gambio Shop').
          label: label || undefined,
          // Vario offlineToken jetzt persistieren - der Navigation-Slot hat ihn später nicht.
          offlineToken: ctx.handshake.offlineToken,
        }),
        signal: abortController.signal,
      });
      const payload = (await res.json()) as InstallResponse;

      if (!res.ok || !payload.ok) {
        setBusy(false);
        setFoot('error', payload.error?.message ?? `HTTP ${res.status}`);
        return;
      }

      showDone(card, baseUrl, payload.channel ?? null);
      sendVarioMessage({ installationComplete: true });
    } catch (err) {
      setBusy(false);
      if ((err as Error).name === 'AbortError') {
        setFoot('info', 'Installation abgebrochen.');
      } else {
        setFoot('error', `Fehler: ${(err as Error).message}`);
      }
    } finally {
      abortController = null;
    }
  });
}

/** Erfolgs-Karte nach abgeschlossener Installation (rein visuell, wie Otto). */
function showDone(card: HTMLElement, baseUrl: string, channel: InstallResponse['channel']) {
  const host = baseUrl.replace(/^https?:\/\//i, '').replace(/\/.*$/, '');
  // Verkaufskanal-Zeile: erfolgreicher Auto-Anlage bestätigen, sonst dezenter Hinweis (Install
  // bleibt erfolgreich — der Kanal lässt sich in den Einstellungen manuell anlegen).
  let channelLine = '';
  if (channel?.ok) {
    const label = channel.label ? escapeHtml(channel.label) : 'Gambio';
    channelLine = `<div class="mono">Verkaufskanal: ${label}${channel.alreadyPresent ? ' (bereits vorhanden)' : ' angelegt'}</div>`;
  } else if (channel && !channel.ok) {
    channelLine = `<div class="mono">Verkaufskanal noch nicht angelegt: ${escapeHtml(channel.error)}. In den Einstellungen über „Kanal anlegen" nachholbar.</div>`;
  }
  card.innerHTML = `
    <div class="done-card">
      <div class="done-ic">${ICON.checkBold}</div>
      <h2>Gambio-App installiert</h2>
      <p>Die Verbindung zu deinem <b>Gambio-Shop</b> ist aktiv. Artikel, Bestellungen und Bestände werden ab jetzt synchronisiert.</p>
      ${host ? `<div class="mono">Shop: ${escapeHtml(host)}</div>` : ''}
      ${channelLine}
    </div>`;
}

/**
 * Abbruch-Karte. Es gibt kein App-Signal, das das Vario-Modal schließt; `installationComplete:
 * true` (im cancel-Handler gesendet) blendet Varios eigenen Schließen-Button ein. Diese Karte
 * macht dem Anwender klar, dass abgebrochen wurde (keine Verbindung angelegt) und er den Dialog
 * über diesen Button beenden kann.
 */
function showCancelled(card: HTMLElement) {
  card.innerHTML = `
    <div class="done-card">
      <div class="done-ic neutral">${ICON.x}</div>
      <h2>Installation abgebrochen</h2>
      <p>Es wurde keine Verbindung angelegt. Du kannst den Dialog jetzt über den Schließen-Button beenden.</p>
    </div>`;
}
